import { ConnectionFailureDto } from '../interfaces/collection-revision-connection.interface';
import { CollectionWorkflowTargetDto } from '../interfaces/collection-revision.interface';

export type CredentialsMode = 'initial' | 'reauth' | 'failed_retry';

export type FailurePresentation = {
  message: string;
  code: string | null;
  occurredAt: string | null;
};

export type IntendedNavigation =
  | { readonly kind: 'previous-target'; readonly targetId: string }
  | { readonly kind: 'next-target'; readonly targetId: string }
  | { readonly kind: 'complete-flow' };

function getLocalizedFailureFallback(activeLang: string): string {
  return activeLang.toLowerCase().startsWith('pt')
    ? 'A tentativa de coleta falhou. Tente novamente.'
    : 'Collection attempt failed. Please try again.';
}

export function getFailurePresentationFromSummary(
  failure: ConnectionFailureDto | null,
  activeLang: string,
): FailurePresentation {
  const fallbackMessage = getLocalizedFailureFallback(activeLang);

  return {
    message: failure?.error.message?.trim() || fallbackMessage,
    code: failure?.error.code?.trim() || null,
    occurredAt: failure?.occurred_at || null,
  };
}

export function getPreviousRevisionTargetIntent(
  targets: readonly CollectionWorkflowTargetDto[],
  currentTargetId: string,
): IntendedNavigation | null {
  const ordered = getOrderedRevisionTargets(targets);
  const currentIndex = requireRevisionTargetIndex(ordered, currentTargetId);
  const previous = ordered[currentIndex - 1];
  return previous
    ? { kind: 'previous-target', targetId: previous.target_id }
    : null;
}

export function getNextRevisionTargetIntent(
  targets: readonly CollectionWorkflowTargetDto[],
  currentTargetId: string,
): IntendedNavigation {
  const ordered = getOrderedRevisionTargets(targets);
  const currentIndex = requireRevisionTargetIndex(ordered, currentTargetId);
  const next = ordered[currentIndex + 1];
  return next
    ? { kind: 'next-target', targetId: next.target_id }
    : { kind: 'complete-flow' };
}

export function getOrderedRevisionTargets(
  targets: readonly CollectionWorkflowTargetDto[],
): readonly CollectionWorkflowTargetDto[] {
  return [...targets].sort(
    (left, right) =>
      left.order_idx_snapshot - right.order_idx_snapshot ||
      left.target_id.localeCompare(right.target_id),
  );
}

function requireRevisionTargetIndex(
  targets: readonly CollectionWorkflowTargetDto[],
  currentTargetId: string,
): number {
  const index = targets.findIndex(
    (target) => target.target_id === currentTargetId,
  );
  if (index < 0) {
    throw new Error(
      `Revision target ${currentTargetId} is absent from the workflow`,
    );
  }
  return index;
}
